import { Form, RadioField } from '@calendly/form';
import { object, string } from '@calendly/form/validation';
import { I18n } from '@calendly/i18n';
import { Loader } from '@calendly/ui/components/loader';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useEffect, useMemo, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DirtyFormTracking } from 'components/core/form';
import { isScimConfiguredSelector, hasVerifiedDomainsSelector } from 'selectors/current_organization';
import { FormFooter } from '../../styles';
import { getDomainControlSettingForOrg, updateDomainControlSetting, getOrganizationAdminsAndOwner } from '../actions';
import { domainControlSettingSelector, accessOwnerDropdownSelector } from '../selectors';
import { AccessOwnerTextInput } from './access_owner_text_input';
import { AdminMembershipInfo } from './admin_membership_info';
import { ORG_MEMBERSHIP_ACCESS_OWNER_TYPE, EXT_LINK_ACCESS_OWNER_TYPE, EXT_EMAIL_ACCESS_OWNER_TYPE, RADIO_BUTTON_NAME } from './constants';
import { OrgMemberAccessOwnerSelectDropdown } from './org_member_access_owner_select_dropdown';
import { DomainControlSettingPreview } from './setting_preview';
import { CancelButton } from '../shared_components/cancel_button';
import { SubmitButton } from '../shared_components/submit_button';
const styledHeadingTextCss = "somlhop";
const StyledHeadingText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'h3';
  return <Component ref={ref} {...restProps} className={classNames(styledHeadingTextCss, props.className)} />;
});
const styledDescriptionTextCss = "s172n3zd";
const StyledDescriptionText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(styledDescriptionTextCss, props.className)} />;
});
const formFieldsContainerCss = "f1qyrfi9";
const FormFieldsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(formFieldsContainerCss, props.className)} />;
});
const styledBannerCss = "sbntq23";
const StyledBanner = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(styledBannerCss, props.className)} />;
});
const noVerifiedDomainsBannerCss = "n1llmxk3";
const NoVerifiedDomainsBanner = forwardRef((props, ref) => <StyledBanner ref={ref} {...props} className={classNames(noVerifiedDomainsBannerCss, props.className)} />);
const scimSuggestionBannerCss = "sqdv1qd";
const ScimSuggestionBanner = forwardRef((props, ref) => <StyledBanner ref={ref} {...props} className={classNames(scimSuggestionBannerCss, props.className)} />);
const styledLinkExampleCss = "sck10hk";
const StyledLinkExample = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(styledLinkExampleCss, props.className)} />;
});
const headerContainerCss = "hh37ysj";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, props.className)} />;
});
export const DomainControlSettings = () => {
  const dispatch = useDispatch();
  const scimEnabled = useSelector(isScimConfiguredSelector);
  const orgHasVerifiedDomains = useSelector(hasVerifiedDomainsSelector);
  const {
    accessOwnerType: initialAccessOwnerType,
    accessOwnerValue: initialAccessOwnerValue,
    hasExistingSetting,
    loading: domainControlSettingLoading
  } = useSelector(domainControlSettingSelector);
  const {
    options: orgMemberOptions,
    loading: orgMembersLoading
  } = useSelector(accessOwnerDropdownSelector);
  const formattedOrgMemberOptions = useMemo(() => orgMemberOptions.map(admin => ({
    name: <AdminMembershipInfo adminMembership={admin} />,
    value: admin.uuid
  })), [orgMemberOptions]);
  const defaultAccessOwnerType = hasExistingSetting ? initialAccessOwnerType || '' : ORG_MEMBERSHIP_ACCESS_OWNER_TYPE;
  const defaultAccessOwnerValue = initialAccessOwnerValue || '';
  useEffect(() => {
    dispatch(getDomainControlSettingForOrg());
    dispatch(getOrganizationAdminsAndOwner());
  }, [dispatch]);
  const validationSchema = useMemo(() => object({
    accessOwnerValue: string().when(RADIO_BUTTON_NAME, {
      is: EXT_LINK_ACCESS_OWNER_TYPE,
      then: string().required().url()
    }).when(RADIO_BUTTON_NAME, {
      is: EXT_EMAIL_ACCESS_OWNER_TYPE,
      then: string().required().email()
    })
  }), []);
  const renderBannerOrBreak = () => {
    if (!orgHasVerifiedDomains) {
      return <NoVerifiedDomainsBanner>
          {I18n.t('publisher.organization_settings.domain_control.domain_control_settings.banners.no_verified_domains')}
        </NoVerifiedDomainsBanner>;
    }
    if (scimEnabled) {
      return <ScimSuggestionBanner>
          {I18n.t('publisher.organization_settings.domain_control.domain_control_settings.banners.scim_suggestion')}
        </ScimSuggestionBanner>;
    }
    return <br />;
  };
  const submitChanges = ({
    accessOwnerType,
    accessOwnerValue
  }) => dispatch(updateDomainControlSetting({
    accessOwnerType,
    accessOwnerValue: accessOwnerValue || ''
  }));
  if (domainControlSettingLoading || orgMembersLoading) {
    return <Loader />;
  }
  return <div data-component="domain-control-setting">
      <HeaderContainer>
        <StyledHeadingText>
          {I18n.t('publisher.organization_settings.domain_control.domain_control_settings.header')}
        </StyledHeadingText>
        <DomainControlSettingPreview />
      </HeaderContainer>
      <StyledDescriptionText>
        {I18n.t('publisher.organization_settings.domain_control.domain_control_settings.description')}
      </StyledDescriptionText>

      {renderBannerOrBreak()}
      <BodyText>{I18n.t('publisher.organization_settings.domain_control.domain_control_settings.form_prompt')}</BodyText>
      <br />
      <Form initialValues={{
      accessOwnerType: defaultAccessOwnerType,
      accessOwnerValue: defaultAccessOwnerValue,
      existingSettingType: defaultAccessOwnerType,
      existingSettingValue: defaultAccessOwnerValue
    }} onSubmit={async (values, actions) => {
      const result = await submitChanges(values);
      if (!result?.error) {
        actions?.resetForm?.({
          values
        });
      }
    }} validationSchema={validationSchema}>
        <DirtyFormTracking />
        <FormFieldsContainer>
          <RadioField label={I18n.t('publisher.organization_settings.domain_control.domain_control_settings.form.org_membership_option')} name={RADIO_BUTTON_NAME} value={ORG_MEMBERSHIP_ACCESS_OWNER_TYPE} />
          <OrgMemberAccessOwnerSelectDropdown hasExistingSetting={hasExistingSetting} options={formattedOrgMemberOptions} />
          <RadioField label={I18n.t('publisher.organization_settings.domain_control.domain_control_settings.form.external_email_option')} name={RADIO_BUTTON_NAME} value={EXT_EMAIL_ACCESS_OWNER_TYPE} />
          <AccessOwnerTextInput label={I18n.t('publisher.organization_settings.domain_control.domain_control_settings.form.email')} placeholder={I18n.t('publisher.organization_settings.domain_control.domain_control_settings.form.external_email_placeholder')} optionField={EXT_EMAIL_ACCESS_OWNER_TYPE} />
          <RadioField label={I18n.t('publisher.organization_settings.domain_control.domain_control_settings.form.external_link_option')} name={RADIO_BUTTON_NAME} value={EXT_LINK_ACCESS_OWNER_TYPE} />
          <StyledLinkExample>
            {I18n.t('publisher.organization_settings.domain_control.domain_control_settings.form.external_link_example')}
          </StyledLinkExample>
          <AccessOwnerTextInput label={I18n.t('publisher.organization_settings.domain_control.domain_control_settings.form.link')} placeholder={I18n.t('publisher.organization_settings.domain_control.domain_control_settings.form.external_link_placeholder')} optionField={EXT_LINK_ACCESS_OWNER_TYPE} />
          <RadioField label={I18n.t('publisher.organization_settings.domain_control.domain_control_settings.form.no_access_option')} name={RADIO_BUTTON_NAME} value="" />
        </FormFieldsContainer>
        <FormFooter>
          <SubmitButton enableOverride={!hasExistingSetting} />
          <CancelButton />
        </FormFooter>
      </Form>
    </div>;
};

require("./domain_control_settings.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./domain_control_settings.js");